import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface PlanoContasGerencial {
  id: number;
  codigoPlanoConta?: string;
  descricaoConta?: string;
  contaMestreId?: number | null;
  tipoContaId?: number | null;      // 0=Sintética, 1=Analítica
  naturezaContaId?: number | null;  // 1=Ativo, 2=Passivo, 3=PatrimônioLíquido, 4=Resultado, 5=Compensação, 9=Outras
  tipoNotaId?: number | null;       // 0=Nenhum, 1=Entrada, 2=Saída
  centroCustoId?: number | null;
  formaPagRecPadraoId?: number | null;
  referencia?: string;
  observacao?: string;
  inativo?: number | null;          // 0=Ativo, 1=Inativo
  ibCompoeDRE?: boolean;
}

export interface PlanoContasGerencialFormData {
  codigoPlanoConta?: string;
  descricaoConta?: string;
  contaMestreId?: number | null;
  tipoContaId?: number | null;
  naturezaContaId?: number | null;
  tipoNotaId?: number | null;
  centroCustoId?: number | null;
  formaPagRecPadraoId?: number | null;
  referencia?: string;
  observacao?: string;
  inativo?: number | null;
  ibCompoeDRE?: boolean;
}

export function usePlanoContasGerencial() {
  const queryClient = useQueryClient();

  const { data: plano = [], isLoading } = useQuery({
    queryKey: ["plano-contas-gerencial"],
    queryFn: async () => {
      const { data } = await apiClient.get<PlanoContasGerencial[]>("/api/plano-contas-gerencial");
      return data;
    },
  });

  const createConta = useMutation({
    mutationFn: async (formData: PlanoContasGerencialFormData) => {
      const { data } = await apiClient.post<PlanoContasGerencial>("/api/plano-contas-gerencial", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plano-contas-gerencial"] });
      toast.success("Conta gerencial criada com sucesso!");
    },
    onError: (error: Error) => toast.error("Erro ao criar conta gerencial: " + error.message),
  });

  const updateConta = useMutation({
    mutationFn: async ({ id, ...formData }: PlanoContasGerencialFormData & { id: number }) => {
      const { data } = await apiClient.put<PlanoContasGerencial>(`/api/plano-contas-gerencial/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plano-contas-gerencial"] });
      toast.success("Conta gerencial atualizada com sucesso!");
    },
    onError: (error: Error) => toast.error("Erro ao atualizar conta gerencial: " + error.message),
  });

  const deleteConta = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/plano-contas-gerencial/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plano-contas-gerencial"] });
      toast.success("Conta gerencial inativada com sucesso!");
    },
    onError: (error: Error) => toast.error("Erro ao inativar conta gerencial: " + error.message),
  });

  return { plano, isLoading, createConta, updateConta, deleteConta };
}
